import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/primitives";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact({ map = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Thank you; we'll be in touch within one working day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly at hello@araturnkey.com");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border-b border-ink/25 bg-transparent py-3.5 text-base text-ink outline-none transition-colors duration-300 placeholder:text-ink/40 focus:border-maroon";

  return (
    <section id="contact" className="bg-paper px-6 py-24 lg:px-12 lg:py-36" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow index="08">Contact</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-ink sm:text-5xl">
              Let's build your <em className="italic text-maroon">next experience</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col gap-8" data-testid="contact-office-details">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">Our Office</p>
                <p className="mt-2 text-base text-ink/85">
                  ARA Turnkey Projects Pvt. Ltd.<br />
                  Flat No 11, Dwarka, Tagore Road, Santacruz (West),<br />
                  Mumbai – 400054, Maharashtra, India
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">Email</p>
                <a
                  href="mailto:hello@araturnkey.com"
                  data-testid="contact-email-link"
                  className="mt-2 inline-block border-b border-ink/30 pb-0.5 text-base text-ink transition-colors duration-300 hover:border-maroon hover:text-maroon"
                >
                  hello@araturnkey.com
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2" data-testid="contact-form">
              <div>
                <label htmlFor="cf-name" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                  Name *
                </label>
                <input id="cf-name" required value={form.name} onChange={set("name")} className={field} placeholder="Your full name" data-testid="contact-input-name" />
              </div>
              <div>
                <label htmlFor="cf-email" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                  Email *
                </label>
                <input id="cf-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="you@company.com" data-testid="contact-input-email" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-phone" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                  Phone
                </label>
                <input id="cf-phone" value={form.phone} onChange={set("phone")} className={field} placeholder="+91" data-testid="contact-input-phone" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                  About your project *
                </label>
                <textarea
                  id="cf-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className={`${field} resize-none`}
                  placeholder="Location, approximate square feet, and what you're planning"
                  data-testid="contact-input-message"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="rounded-full bg-maroon px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:scale-[1.03] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>

      {map && (
        <div className="mx-auto mt-24 max-w-[1440px]" data-testid="contact-map">
          <Reveal>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-ink/50">
              <span className="text-maroon">@</span> · Find us
            </p>
            <div className="mt-8 overflow-hidden border border-ink/15">
              <iframe
                title="ARA Turnkey Projects · office location"
                src="https://maps.google.com/maps?q=Tagore%20Road%2C%20Santacruz%20West%2C%20Mumbai%20400054&z=15&output=embed"
                className="h-[380px] w-full lg:h-[440px]"
                style={{ filter: "grayscale(1) contrast(1.05)" }}
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      )}
    </section>
  );
}
